// export default AppContext;
// ------------------------------------------------------------------------
import React, { createContext, useEffect, useState, useCallback } from "react";
import Cookies from "js-cookie";
import { ClientDetails } from "./apis/auth/index";
import {
  Settings,
  AllGames,
  OnlyLiveGamesMatches,
  MarketAnalysisLiveGamesMatches,
} from "./apis/common/index";
import { CookiesName } from "./common";
import { io } from "socket.io-client";
import { LiveBets, UnmatchBets } from "./apis/bets";
import useIdleLogout from "./useIdleLogout";

// Auto-logout window. Reset on any user interaction. 60 minutes.
const IDLE_LOGOUT_MS = 60 * 60 * 1000;

export const Context = createContext();

const AppContext = ({ children }) => {
  // Use environment variables for different environments
  // const IMG_URL = "http://127.0.0.1:1802";
  // const SOCKET = "ws://127.0.0.1:1802";

  // const IMG_URL = "http://192.168.16.53:1802";
  // const SOCKET = "ws://192.168.16.53:1802";

  // const IMG_URL = "http://localhost:1802";
  // const SOCKET = "ws://localhost:1802";

  // # # Production Environment Variable

  const IMG_URL = "https://node1.londoncrownexchange.com";
  const SOCKET = "wss://node1.londoncrownexchange.com";

  // State declarations
  const [ChangePass, setChangePass] = useState(false);
  const [User, setUser] = useState({});
  const [LiveAmount, setLiveAmount] = useState([]);
  const [AppSetup, setAppSetup] = useState({});
  const [Games, setGames] = useState([]);
  const [Markets, setMarkets] = useState([]);
  const [Fancy, setFancy] = useState([]);
  const [Scorecard, setScorecard] = useState({});
  const [BallEvent, setBallEvent] = useState({});
  const [LiveGamesMatches, setLiveGamesMatches] = useState([]);
  const [
    MarketAnalysisLiveGamesMatche,
    setMarketAnalysisLiveGamesMatches,
  ] = useState([]);
  const [socket, setSocket] = useState(null);
  const [currentGameId, setCurrentGameId] = useState(null);
  const [Cart, setCart] = useState({});
  const [matched_bets, setmatched_bets] = useState([]);
  const [unmatched_bets, setunmatched_bets] = useState([]);
  const [loading, setLoading] = useState({
    user: false,
    games: false,
    bets: false,
  });

  // Track the auth cookie so the idle-logout hook can enable/disable itself.
  // Re-read it on the User-change effect below; this is fine because cookies
  // are very cheap to read and the value only changes on login/logout.
  const token = Cookies.get(CookiesName);

  // Auto-logout after IDLE_LOGOUT_MS of inactivity.
  const handleIdleLogout = useCallback(() => {
    try {
      Cookies.remove(CookiesName, { path: "/" });
    } catch (_) {
      // best-effort; cookie may already be gone
    }
    setUser({});
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  }, []);

  useIdleLogout({
    enabled: !!token,
    timeoutMs: IDLE_LOGOUT_MS,
    onTimeout: handleIdleLogout,
  });

  // WebSocket connection
  useEffect(() => {
    const newSocket = io(SOCKET, {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      transports: ["websocket"],
    });

    setSocket(newSocket);

    newSocket.on("connect", () => {
      console.log("Connected to WebSocket server");
    });

    newSocket.on("matchodds", (data) => {
      console.log("Received matchodds data:", data);
      setMarkets(data);
    });

    newSocket.on("fancyodds", (data) => {
      // console.log("Received fancyodds data:", data);
      setFancy(data);
    });

    newSocket.on("disconnect", () => {
      console.log("Disconnected from WebSocket server");
    });

    newSocket.on("connect_error", (error) => {
      console.error("Connection error:", error);
    });

    newSocket.on("error", (error) => {
      console.error("WebSocket error:", error);
    });

    return () => {
      // Leave current game room before disconnecting
      if (currentGameId) {
        newSocket.emit("leave_game", { game_id: currentGameId });
      }
      newSocket.disconnect();
    };
  }, [SOCKET]);

  // API functions with useCallback to prevent unnecessary re-renders
  const GETUser = useCallback(async () => {
    const token = Cookies.get(CookiesName);
    if (!token) return;

    setLoading((prev) => ({ ...prev, user: true }));
    try {
      const response = await ClientDetails();
      if (response?.success) {
        setChangePass(response?.data?.user_details?.change_password || false);
        setUser(response?.data?.user_details || {});
      }
    } catch (error) {
      console.error("Error fetching user:", error);
    } finally {
      setLoading((prev) => ({ ...prev, user: false }));
    }
  }, []);

  const GETSettings = useCallback(async () => {
    try {
      const response = await Settings();
      if (response?.success) {
        setAppSetup(response?.data || {});
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  }, []);

  const GETAllGames = useCallback(async () => {
    setLoading((prev) => ({ ...prev, games: true }));
    try {
      const response = await AllGames();
      if (response?.success) {
        setGames(response?.data || []);
      }
    } catch (error) {
      console.error("Error fetching games:", error);
    } finally {
      setLoading((prev) => ({ ...prev, games: false }));
    }
  }, []);

  const GETOnlyLiveGamesMatches = useCallback(async () => {
    try {
      const [liveMatchesRes, marketAnalysisRes] = await Promise.all([
        OnlyLiveGamesMatches(),
        MarketAnalysisLiveGamesMatches(),
      ]);

      if (liveMatchesRes?.success) {
        setLiveGamesMatches(liveMatchesRes?.data || []);
      }

      if (marketAnalysisRes?.success) {
        setMarketAnalysisLiveGamesMatches(marketAnalysisRes?.data || []);
      }
    } catch (error) {
      console.error("Error fetching live matches:", error);
    }
  }, []);

  const GetBets = useCallback(async () => {
    if (!User?.id) return;

    setLoading((prev) => ({ ...prev, bets: true }));
    try {
      const [liveBetsRes, unmatchBetsRes] = await Promise.all([
        LiveBets(),
        UnmatchBets(),
      ]);

      setmatched_bets(liveBetsRes?.data || []);
      setunmatched_bets(unmatchBetsRes?.data || []);
    } catch (error) {
      console.error("Error fetching bets:", error);
    } finally {
      setLoading((prev) => ({ ...prev, bets: false }));
    }
  }, [User?.id]);

  // Join a game room to receive updates only for that game
  const joinGame = useCallback((gameId) => {
    if (!socket || !gameId) return;
    
    // Leave previous game if different
    if (currentGameId && currentGameId !== gameId) {
      socket.emit("leave_game", { game_id: currentGameId });
    }
    console.log("Joining game aaaaaa:", gameId);
    
    socket.emit("join_game", { game_id: gameId });
    setCurrentGameId(gameId);
    console.log(`Joined game room: ${gameId}`);
  }, [socket, currentGameId]);

  // Leave the current game room
  const leaveGame = useCallback(() => {
    if (!socket || !currentGameId) return;
    
    socket.emit("leave_game", { game_id: currentGameId });
    setCurrentGameId(null);
    console.log(`Left game room: ${currentGameId}`);
  }, [socket, currentGameId]);

  // Initial data fetching
  useEffect(() => {
    GETSettings();
    GETUser();
  }, [GETSettings, GETUser]);

  // Polling for user data
  useEffect(() => {
    const intervalId = setInterval(GETUser, 3000);
    return () => clearInterval(intervalId);
  }, [GETUser]);

  // Polling for games data
  useEffect(() => {
    GETAllGames();
    GETOnlyLiveGamesMatches();

    const gamesInterval = setInterval(GETAllGames, 10000);
    const matchesInterval = setInterval(GETOnlyLiveGamesMatches, 10000);

    return () => {
      clearInterval(gamesInterval);
      clearInterval(matchesInterval);
    };
  }, [GETAllGames, GETOnlyLiveGamesMatches]);

  // Polling for bets data
  useEffect(() => {
    GetBets();

    const betsInterval = setInterval(GetBets, 5000);
    return () => clearInterval(betsInterval);
  }, [GetBets]);

  return (
    <Context.Provider
      value={{
        // URLs
        IMG_URL,
        SOCKET,

        // Cookies utility
        Cookies,

        // App Setup
        AppSetup,

        // User
        User,
        setUser,
        GETUser,

        // Games
        Games,

        // Markets & Fancy
        Markets,
        Fancy,

        // Live Matches
        LiveGamesMatches,
        MarketAnalysisLiveGamesMatche,

        // Cart
        Cart,
        setCart,

        // Scorecard & Events
        Scorecard,
        BallEvent,

        // Live Amount
        LiveAmount,
        setLiveAmount,

        // Password Change
        ChangePass,
        setChangePass,

        // Bets
        matched_bets,
        unmatched_bets,

        // Loading States
        loading,

        // Socket
        socket,

        // Game Room Management
        currentGameId,
        joinGame,
        leaveGame,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default AppContext;
